import React, { useEffect, useState } from "react";
import {
  PORTAL_LIFECYCLE_PHASE,
  msUntilResendAllowed,
} from "../../utils/portalEmailVerificationModel";

const STEPS = [
  { key: "account", label: "Create your secure account" },
  { key: "verify", label: "Verify your email" },
  { key: "ready", label: "Your portal is ready" },
];

export default function PortalEmailVerificationPanel({
  email = "",
  lifecyclePhase = PORTAL_LIFECYCLE_PHASE.VERIFICATION_EMAIL_SENT,
  busy = false,
  resendCooldownMs = 0,
  error = "",
  notice = "",
  onOpenEmail,
  onResend,
  onVerified,
  onSignOut,
}) {
  const [cooldownMs, setCooldownMs] = useState(resendCooldownMs);

  useEffect(() => {
    setCooldownMs(resendCooldownMs);
    if (resendCooldownMs <= 0) return undefined;
    const timerId = window.setInterval(() => {
      setCooldownMs((prev) => (prev <= 1000 ? 0 : prev - 1000));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [resendCooldownMs]);

  const activeStep = lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_REQUIRED ? 0 : 1;
  const resendDisabled = busy || cooldownMs > 0;
  const isSent = lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_EMAIL_SENT;
  const resendLabel =
    cooldownMs > 0
      ? `Resend available in ${Math.ceil(cooldownMs / 1000)}s`
      : isSent ? "Resend verification email" : "Send verification email";

  return (
    <div className="min-h-[calc(100vh-2rem)] bg-slate-100/90 py-10 px-4 md:px-6">
      <div className="mx-auto max-w-lg">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md md:p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Client Portal</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">Verify your email</h1>
          {lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_EMAIL_SENT ? (
            <p className="mt-2 text-sm text-slate-600">
              We sent a verification link to <span className="font-medium text-slate-900">{email}</span>. Confirm your
              email before we finish setting up your portal access.
            </p>
          ) : (
            <p className="mt-2 text-sm text-slate-600">
              A verification link is required to continue. Send a verification email to{" "}
              <span className="font-medium text-slate-900">{email}</span> and confirm your email to access your portal.
            </p>
          )}

          <ol className="mt-6 space-y-3">
            {STEPS.map((step, index) => {
              const done = index < activeStep;
              const current = index === activeStep;
              return (
                <li
                  key={step.key}
                  className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
                    current
                      ? "border-slate-800 bg-slate-50 text-slate-900"
                      : done
                        ? "border-emerald-200 bg-emerald-50 text-emerald-950"
                        : "border-slate-200 text-slate-500"
                  }`}
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-semibold">
                    {done ? "✓" : index + 1}
                  </span>
                  <span>{step.label}</span>
                </li>
              );
            })}
          </ol>

          {error ? (
            <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          ) : null}
          {notice ? (
            <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">{notice}</p>
          ) : null}

          <div className="mt-6 flex flex-col gap-3">
            {onOpenEmail ? (
              <button
                type="button"
                className="w-full min-h-11 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
                onClick={onOpenEmail}
                disabled={busy}
              >
                Open my email
              </button>
            ) : null}
            <button
              type="button"
              className="w-full min-h-11 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-50"
              onClick={onVerified}
              disabled={busy}
            >
              {busy ? "Checking…" : "I've verified my email"}
            </button>
            <button
              type="button"
              className="w-full min-h-11 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              onClick={onResend}
              disabled={resendDisabled}
            >
              {resendLabel}
            </button>
            {onSignOut ? (
              <button
                type="button"
                className="text-sm font-medium text-slate-700 underline decoration-slate-400 underline-offset-[3px] hover:text-slate-900 disabled:opacity-50"
                onClick={onSignOut}
                disabled={busy}
              >
                Sign out and start over
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function formatResendCooldown(lastSentAtMs, nowMs = Date.now()) {
  return msUntilResendAllowed(lastSentAtMs, nowMs);
}
